/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useEffect, useRef} from 'react';
import {callOpenRouterApi} from '../../services/openrouterApi';
import {UserProfile, ChatMessage as ChatMessageType} from '../../types';
import {getAdaptiveTutorQuizPrompt, getAdaptiveTutorLessonPrompt, getAdaptiveTutorNextSegmentPrompt} from '../../utils/prompts';
import {supabase} from '../../services/supabase';
import {CreditService} from '../../services/creditService';
import ChatMessage from '../common/ChatMessage';
import InputField from '../common/InputField';
import SubscriptionModal from '../common/SubscriptionModal';
import {validateLaTeX} from '../../utils/mathUtils';
import {useTTS} from '../../contexts/TTSContext';
import '../../styles/components/AdaptiveTutor.css';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
}

interface QuizState {
  questions: QuizQuestion[];
  currentQuestion: number;
  answers: number[];
  isComplete: boolean;
  topic: string;
  workplace: string;
}

interface LessonSegment {
  id: string;
  title: string;
  content: string;
  gapIndex: number;
  requiresCredit: boolean;
  consumed: boolean;
}

interface ChatSession {
  id: string;
  title: string;
  timestamp: Date;
  messages: ChatMessageType[];
  isTemporary?: boolean;
  quiz_completed?: boolean;
  quiz_results?: string;
  quiz_answers?: number[];
  quiz_questions?: QuizQuestion[];
  topic?: string;
  workplace?: string;
  current_gap_index?: number;
  total_gaps?: number;
  lesson_segments?: LessonSegment[];
}

interface AdaptiveTutorProps {
  userProfile: UserProfile;
  sessionId?: string;
}

// Utility function for parsing comma-separated values
const parseCommaSeparated = (value: string): string[] => {
  return value
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0);
};

const AdaptiveTutor: React.FC<AdaptiveTutorProps> = ({userProfile, sessionId}) => {
  // Session and chat states
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  
  // Credit and subscription states
  const [userCredits, setUserCredits] = useState<number>(0);
  const [subscriptionTier, setSubscriptionTier] = useState<string>('free');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isCheckingCredits, setIsCheckingCredits] = useState(true);
  
  // Configuration states
  const [configStep, setConfigStep] = useState<'welcome' | 'details' | 'quiz'>('welcome');
  const [config, setConfig] = useState({
    course: '',
    topic: '',
    keywords: '',
    workplace: ''
  });
  
  // Temporary state for comma-separated input
  const [tempKeywords, setTempKeywords] = useState('');
  
  // Quiz-specific states
  const [quizState, setQuizState] = useState<QuizState | null>(null);
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
  const [gapAnalysis, setGapAnalysis] = useState<any[]>([]);
  const [lessonSegments, setLessonSegments] = useState<LessonSegment[]>([]);
  const [currentGapIndex, setCurrentGapIndex] = useState(0);
  
  const {speak, isMessageSpeaking} = useTTS();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, quizState]);

  // Load initial data
  useEffect(() => {
    isMountedRef.current = true;
    
    const loadData = async () => {
      if (isMountedRef.current) {
        await loadUserCredits();
        await loadSessions();
      }
    };
    
    loadData();
    
    const handleResize = () => {
      if (isMountedRef.current) {
        setSidebarOpen(window.innerWidth > 768);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load user credits and subscription info
  const loadUserCredits = async () => {
    try {
      const {data: {user}} = await supabase.auth.getUser();
      if (!user) {
        setIsCheckingCredits(false);
        return;
      }

      const {data: profile} = await supabase
        .from('profiles')
        .select('credits, subscription_tier')
        .eq('id', user.id)
        .single();

      if (isMountedRef.current) {
        setUserCredits(profile?.credits || 0);
        setSubscriptionTier(profile?.subscription_tier || 'free');
        setIsCheckingCredits(false);
      }
    } catch (error) {
      console.error('Error in loadUserCredits:', error);
      setIsCheckingCredits(false);
    }
  };

  // Check if user has enough credits
  const checkCredits = async (requiredCredits: number = 1): Promise<boolean> => {
    try {
      const {data: {user}} = await supabase.auth.getUser();
      if (!user) {
        setErrorMessage('Please log in to continue');
        return false;
      }

      const canGenerate = await CreditService.canGenerateExam(user.id);
      
      if (isMountedRef.current) {
        setUserCredits(canGenerate.credits);
      }
      
      if (!canGenerate.canGenerate) {
        setErrorMessage(`You need at least ${requiredCredits} credit(s) to continue. You have ${canGenerate.credits} credits.`);
        setShowSubscriptionModal(true);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking credits:', error);
      setErrorMessage('Error checking credits. Please try again.');
      return false;
    }
  };

  // Consume credits
  const consumeCredits = async (amount: number = 1, description: string): Promise<boolean> => {
    try {
      const {data: {user}} = await supabase.auth.getUser();
      if (!user) {
        setErrorMessage('User not authenticated');
        return false;
      }

      const creditResult = await CreditService.consumeCredits(
        user.id,
        'adaptive',
        description
      );

      if (!creditResult.success) {
        setErrorMessage('Failed to consume credit. Please try again.');
        return false;
      }

      if (isMountedRef.current) {
        setUserCredits(creditResult.remainingCredits);
      }

      return true;
    } catch (error) {
      console.error('Error consuming credit:', error);
      setErrorMessage('Failed to process credit. Please try again.');
      return false;
    }
  };

  const loadSessions = async () => {
    try {
      const {data: {user}} = await supabase.auth.getUser();
      if (!user) {
        console.log('User not authenticated');
        return;
      }

      const {data: sessionData} = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('chat_type', 'adaptive-tutor')
        .order('created_at', {ascending: false});

      const loadedSessions: ChatSession[] = await Promise.all(
        (sessionData || []).map(async (session) => {
          const {data: msgs} = await supabase
            .from('chat_messages')
            .select('*')
            .eq('session_id', session.id)
            .order('created_at', {ascending: true});

          return {
            id: session.id,
            title: session.title,
            timestamp: new Date(session.created_at),
            messages: msgs?.map(msg => ({
              id: msg.id?.toString() || Date.now().toString(),
              role: msg.role,
              content: msg.content,
              timestamp: new Date(msg.created_at),
              chat_type: 'adaptive-tutor',
            })) || [],
            isTemporary: false,
            quiz_completed: session.quiz_completed || false,
            quiz_results: session.quiz_results,
            quiz_answers: session.quiz_answers,
            quiz_questions: session.quiz_questions,
            topic: session.topic,
            workplace: session.workplace,
            current_gap_index: session.current_gap_index || 0,
            total_gaps: session.total_gaps,
            lesson_segments: session.lesson_segments,
          };
        })
      );

      if (isMountedRef.current) {
        setSessions(loadedSessions);

        if (loadedSessions.length > 0) {
          const initialSession = sessionId 
            ? loadedSessions.find(s => s.id === sessionId) || loadedSessions[0]
            : loadedSessions[0];
          setCurrentSessionId(initialSession.id);
          setMessages(initialSession.messages);
          
          if (initialSession.quiz_completed) {
            setConfigStep('quiz');
            if (initialSession.quiz_questions) {
              setQuizState({
                questions: initialSession.quiz_questions,
                currentQuestion: 0,
                answers: initialSession.quiz_answers || [],
                isComplete: true,
                topic: initialSession.topic || '',
                workplace: initialSession.workplace || ''
              });
            }
            if (initialSession.lesson_segments) {
              setLessonSegments(initialSession.lesson_segments);
              setCurrentGapIndex(initialSession.current_gap_index || 0);
            }
          } else {
            setConfigStep('welcome');
          }
        } else {
          createNewSession();
        }
      }
    } catch (error) {
      console.error('Error in loadSessions:', error);
    }
  };

  const createNewSession = () => {
    const tempSession: ChatSession = {
      id: `temp-${Date.now()}`,
      title: 'New Adaptive Session',
      timestamp: new Date(),
      messages: [],
      isTemporary: true,
      quiz_completed: false,
    };
    
    if (isMountedRef.current) {
      setSessions(prev => [tempSession, ...prev]);
      setCurrentSessionId(tempSession.id);
      setMessages([]);
      setConfigStep('welcome');
      setConfig({ course: '', topic: '', keywords: '', workplace: '' });
      setTempKeywords('');
      setQuizState(null);
      setLessonSegments([]);
      setCurrentGapIndex(0);
      setGapAnalysis([]);
    }
  };

  const saveChatSession = async (session: ChatSession) => {
    const {data: {user}} = await supabase.auth.getUser();
    if (!user) {
      setErrorMessage('Failed to authenticate user. Please log in again.');
      return null;
    }

    const {data, error} = await supabase
      .from('chat_sessions')
      .insert({
        user_id: user.id,
        title: session.title,
        chat_type: 'adaptive-tutor',
        created_at: session.timestamp.toISOString(),
        quiz_completed: session.quiz_completed || false,
        quiz_results: session.quiz_results,
        quiz_answers: session.quiz_answers,
        quiz_questions: session.quiz_questions,
        topic: session.topic,
        workplace: session.workplace,
        current_gap_index: session.current_gap_index,
        total_gaps: session.total_gaps,
        lesson_segments: session.lesson_segments,
      })
        .select()
        .single();

    if (error) {
      setErrorMessage('Error creating chat session: ' + error.message);
      console.error('Error creating chat session:', error);
      return null;
    }

    return {...session, id: data.id, isTemporary: false};
  };

  const saveChatMessage = async (sessionId: string, role: 'user' | 'assistant', content: string) => {
    if (!sessionId || sessionId.startsWith('temp-')) {
      return null;
    }

    const {data, error} = await supabase
      .from('chat_messages')
      .insert({
        session_id: sessionId,
        role,
        content,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving message:', error);
      return null;
    }

    return data;
  };

  const updateSession = async (sessionId: string, updates: any) => {
    if (!sessionId || sessionId.startsWith('temp-')) return;

    const {error} = await supabase
      .from('chat_sessions')
      .update(updates)
      .eq('id', sessionId);

    if (error) {
      console.error('Error updating session:', error);
    }
  };

  const generateChatTitle = (course: string, topic: string): string => {
    return `${course}: ${topic.substring(0, 30)}${topic.length > 30 ? '...' : ''}`;
  };

  const handleKeywordsBlur = () => {
    setConfig(prev => ({
      ...prev,
      keywords: tempKeywords
    }));
  };

  const handleStartQuiz = async () => {
    if (!config.course.trim() || !config.topic.trim()) {
      setErrorMessage('Please enter both course/subject and topic');
      return;
    }

    const hasCredits = await checkCredits(1);
    if (!hasCredits) return;

    setIsGeneratingQuiz(true);
    setIsLoading(true);
    setErrorMessage('');

    let newSessionId = currentSessionId;
    let isNewSession = messages.length === 0 && sessions.find(s => s.id === currentSessionId)?.isTemporary;

    // Consume credit for quiz generation
    const creditConsumed = await consumeCredits(1, `Quiz: ${config.course} - ${config.topic}`);
    if (!creditConsumed) {
      setIsLoading(false);
      setIsGeneratingQuiz(false);
      return;
    }

    // Save session if new
    if (isNewSession) {
      const tempSession = sessions.find(s => s.id === currentSessionId);
      if (tempSession) {
        const title = generateChatTitle(config.course, config.topic);
        const sessionToSave = {
          ...tempSession,
          title,
          topic: config.topic,
          workplace: config.workplace
        };
        const savedSession = await saveChatSession(sessionToSave);
        if (!savedSession) {
          setIsLoading(false);
          setIsGeneratingQuiz(false);
          return;
        }
        newSessionId = savedSession.id;
        if (isMountedRef.current) {
          setSessions(prev => prev.map(s => s.id === currentSessionId ? savedSession : s));
          setCurrentSessionId(newSessionId);
        }
      }
    }

    // Add user message
    const userMessageContent = `I want to learn about ${config.topic} in ${config.course}${config.workplace ? ` for application in ${config.workplace}` : ''}.`;
    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: userMessageContent,
      timestamp: new Date(),
      chat_type: 'adaptive-tutor',
    };

    const newMessages = [...messages, userMessage];
    if (isMountedRef.current) {
      setMessages(newMessages);
    }
    await saveChatMessage(newSessionId, 'user', userMessage.content);

    try {
      const keywords = parseCommaSeparated(config.keywords);
      
      const quizPrompt = getAdaptiveTutorQuizPrompt(
        userProfile,
        config.course,
        config.topic,
        keywords,
        config.workplace
      );

      const systemMessage = `You are a quiz generator for BB EdTech's Adaptive Tutor. Generate EXACTLY 10 multiple-choice questions to assess the learner's understanding of ${config.topic} in ${config.course}.

CRITICAL: Respond ONLY with valid JSON. No preamble, no markdown, no backticks. Just the JSON object.

Format:
{
  "questions": [
    {
      "question": "Question text here?",
      "options": ["Option 1", "Option 2", "Option 3", "Option 4", "I don't know / I need to learn this"],
      "correctAnswer": 0
    }
  ]
}

Rules:
- Exactly 10 questions
- Each question has exactly 5 options
- The 5th option MUST always be "I don't know / I need to learn this"
- Options 0-4 should be plausible but with only ONE correct answer
- Make some options confusing but clearly wrong for those who understand
- correctAnswer is the index (0-4) of the correct option
- Questions should progressively test deeper understanding
- Include questions that relate to real-world applications in ${config.workplace || 'relevant contexts'}`;

      const response = await callOpenRouterApi(quizPrompt, userProfile, systemMessage);
      
      let cleanResponse = response.trim();
      cleanResponse = cleanResponse.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      
      const quizData = JSON.parse(cleanResponse);

      if (!quizData.questions || quizData.questions.length !== 10) {
        throw new Error('Invalid quiz format: must have exactly 10 questions');
      }

      if (isMountedRef.current) {
        setQuizState({
          questions: quizData.questions,
          currentQuestion: 0,
          answers: [],
          isComplete: false,
          topic: config.topic,
          workplace: config.workplace
        });
        setConfigStep('quiz');
      }

      await updateSession(newSessionId, {
        quiz_questions: quizData.questions,
        topic: config.topic,
        workplace: config.workplace
      });

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Great! I've prepared a 10-question quiz to assess your current understanding of **${config.topic}** in **${config.course}**. This will help me identify any gaps and create a personalized lesson tailored to your needs${config.workplace ? `, with practical applications for **${config.workplace}**` : ''}.\n\n**Instructions:**\n- Answer each question honestly\n- If you're unsure, select "I don't know / I need to learn this"\n- There's no penalty for not knowing—this helps me teach you better!\n\nLet's begin! 🎯`,
        timestamp: new Date(),
        chat_type: 'adaptive-tutor',
      };

      const updatedMessages = [...newMessages, assistantMessage];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }
      await saveChatMessage(newSessionId, 'assistant', assistantMessage.content);

    } catch (error) {
      console.error('Error generating quiz:', error);
      setErrorMessage('Failed to generate quiz. Please try again with a different topic.');
      
      const errorMsg: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error while generating the quiz. Please try again with a clearer or simpler topic.',
        timestamp: new Date(),
        chat_type: 'adaptive-tutor',
      };
      
      const updatedMessages = [...newMessages, errorMsg];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }
      await saveChatMessage(newSessionId, 'assistant', errorMsg.content);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        setIsGeneratingQuiz(false);
      }
    }
  };

  const handleQuizAnswer = async (answerIndex: number) => {
    if (!quizState || isLoading) return;

    const newAnswers = [...quizState.answers, answerIndex];
    const nextQuestion = quizState.currentQuestion + 1;

    if (nextQuestion < quizState.questions.length) {
      if (isMountedRef.current) {
        setQuizState({
          ...quizState,
          currentQuestion: nextQuestion,
          answers: newAnswers,
        });
      }
    } else {
      // Quiz complete
      if (isMountedRef.current) {
        setQuizState({
          ...quizState,
          answers: newAnswers,
          isComplete: true,
        });
      }

      setIsLoading(true);

      // Calculate scores
      const correctCount = newAnswers.filter((ans, idx) => 
        ans === quizState.questions[idx].correctAnswer
      ).length;

      const iDontKnowCount = newAnswers.filter(ans => ans === 4).length;
      const incorrectCount = 10 - correctCount - iDontKnowCount;

      // Analyze gaps
      const analysis = quizState.questions.map((q, idx) => ({
        question: q.question,
        userAnswer: q.options[newAnswers[idx]],
        correctAnswer: q.options[q.correctAnswer],
        isCorrect: newAnswers[idx] === q.correctAnswer,
        isIDontKnow: newAnswers[idx] === 4,
        topic: `Question ${idx + 1}`,
        misconception: !(newAnswers[idx] === q.correctAnswer || newAnswers[idx] === 4)
      }));

      setGapAnalysis(analysis);

      const quizResultsSummary = `Score: ${correctCount}/10 correct | ${iDontKnowCount} knowledge gaps | ${incorrectCount} misconceptions`;
      
      await updateSession(currentSessionId, {
        quiz_completed: true,
        quiz_results: quizResultsSummary,
        quiz_answers: newAnswers,
        total_gaps: iDontKnowCount + incorrectCount
      });

      const resultsMessage: ChatMessageType = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `**Quiz Complete!** 🎉\n\n**Results:**\n- ✅ Correct: ${correctCount}/10\n- 🤔 Knowledge gaps (admitted): ${iDontKnowCount}\n- ❌ Misconceptions: ${incorrectCount}\n\nNow analyzing your responses to identify knowledge gaps and craft a personalized lesson...`,
        timestamp: new Date(),
        chat_type: 'adaptive-tutor',
      };

      const newMessages = [...messages, resultsMessage];
      if (isMountedRef.current) {
        setMessages(newMessages);
      }
      await saveChatMessage(currentSessionId, 'assistant', resultsMessage.content);

      // Generate first lesson segment (free - included in quiz credit)
      await generateFirstLessonSegment(analysis, quizState.topic, quizState.workplace);
    }
  };

  const generateFirstLessonSegment = async (analysis: any[], topic: string, workplace: string) => {
    try {
      // Find first gap (either "I don't know" or incorrect answer)
      const firstGapIndex = analysis.findIndex(a => !a.isCorrect);
      
      if (firstGapIndex === -1) {
        // No gaps - learner knows everything!
        const perfectLessonMessage: ChatMessageType = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `## 📚 Perfect Score! 🎉\n\nYou answered all questions correctly! You have a solid understanding of **${topic}**. However, let me provide you with some advanced insights and practical applications${workplace ? ` in **${workplace}**` : ''}.\n\n### Key Applications\n\n${workplace ? `In your work at **${workplace}**, these concepts can be applied to:` : 'These concepts can be applied to:'}\n\n- Streamline processes and improve efficiency\n- Solve complex problems with confidence\n- Innovate and create new solutions\n\n### Next Steps\n\nWould you like to explore:\n1. Advanced topics in this area?\n2. Practical case studies?\n3. Related concepts to broaden your knowledge?`,
          timestamp: new Date(),
          chat_type: 'adaptive-tutor',
        };

        const newMessages = [...messages, perfectLessonMessage];
        if (isMountedRef.current) {
          setMessages(newMessages);
        }
        await saveChatMessage(currentSessionId, 'assistant', perfectLessonMessage.content);
        setIsLoading(false);
        return;
      }

      // Get the lesson prompt from our utility
      const lessonPrompt = getAdaptiveTutorLessonPrompt(
        userProfile,
        topic,
        'intermediate',
        analysis,
        workplace
      );

      // Enhanced system message with explicit formatting instructions
      const systemMessage = `You are BB EdTech's Adaptive Tutor. Create the FIRST PART of a personalized lesson based on the quiz analysis.

IMPORTANT FORMATTING REQUIREMENTS - YOU MUST FOLLOW THESE EXACTLY:

1. **USE TABLES** for all comparative data:
   - Summary table with | Metric | Result | format
   - Misconceptions table with | # | Wrong Idea | Correct Understanding | format
   - Use |---| for column separators

2. **USE MARKDOWN HEADINGS** exactly as shown:
   - ## for main sections (Quiz Summary, Focus Area, Quick Lesson, etc.)
   - No deeper nesting than ###

3. **USE EMOJIS** for visual cues: 📊 🎯 📚 💼 📋 ⏭️

4. **KEEP IT CONCISE** - maximum 3-4 sentences per section

5. **ALWAYS include** the "Next" section with credit reminder

Here's the EXACT structure you must follow:

## 📊 Quiz Summary

| Metric | Result |
|--------|--------|
| ✅ Mastered | X/10 |
| 🤔 Gaps | X/10 |
| ❌ Misconceptions | X/10 |

---

## 🎯 Focus Area: [Topic Name]

**Question:** [Question text]

**You answered:** "[User's answer]"
**Correct answer:** "[Correct answer]"

---

## 📚 Quick Lesson

[2-3 clear sentences explaining the concept]

**Example:** [One concrete example]

---

## 💼 At Work: [Workplace or "Your Field"]

*In [Location]:* [1-2 sentences on practical application]

---

## 📋 Common Misconceptions

| # | Wrong Idea | Correct Understanding |
|---|------------|----------------------|
| 1 | [Misconception] | [Correct concept] |

---

## ⏭️ Next

*Module 1 done. Type "continue" for Module 2 (0.5 credits).*

---

Focus ONLY on the first knowledge gap identified.`;

      const lessonResponse = await callOpenRouterApi(
        lessonPrompt,
        userProfile,
        systemMessage
      );

      const finalLesson = validateLaTeX(lessonResponse);

      // Store lesson segments
      const segments: LessonSegment[] = [
        {
          id: `seg-${Date.now()}-0`,
          title: `Understanding ${analysis[firstGapIndex].topic}`,
          content: finalLesson,
          gapIndex: firstGapIndex,
          requiresCredit: false,
          consumed: true
        }
      ];

      setLessonSegments(segments);
      setCurrentGapIndex(firstGapIndex);

      await updateSession(currentSessionId, {
        lesson_segments: segments,
        current_gap_index: firstGapIndex
      });

      const lessonMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: finalLesson,
        timestamp: new Date(),
        chat_type: 'adaptive-tutor',
      };

      const newMessages = [...messages, lessonMessage];
      if (isMountedRef.current) {
        setMessages(newMessages);
      }
      await saveChatMessage(currentSessionId, 'assistant', finalLesson);

    } catch (error) {
      console.error('Error generating lesson:', error);
      setErrorMessage('Failed to generate personalized lesson.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateNextLessonSegment = async () => {
    if (!gapAnalysis.length) return;

    const hasCredits = await checkCredits(0.5);
    if (!hasCredits) return;

    setIsLoading(true);

    // Find next gap
    const nextGapIndex = gapAnalysis.findIndex((a, idx) => 
      idx > currentGapIndex && !a.isCorrect
    );

    if (nextGapIndex === -1) {
      // No more gaps
      const completionMessage: ChatMessageType = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `## 🎉 Congratulations!\n\nYou've addressed all the knowledge gaps identified in your quiz! You now have a comprehensive understanding of **${config.topic}**.\n\n### Summary of What You've Learned\n\nYou've explored the key concepts, addressed misconceptions, and learned how to apply these ideas in practical contexts.\n\n### Next Steps\n\nWould you like to:\n1. Take another quiz on a related topic?\n2. Explore advanced concepts?\n3. Practice with real-world scenarios?\n\nJust let me know what you'd like to do next!`,
        timestamp: new Date(),
        chat_type: 'adaptive-tutor',
      };

      const newMessages = [...messages, completionMessage];
      if (isMountedRef.current) {
        setMessages(newMessages);
      }
      await saveChatMessage(currentSessionId, 'assistant', completionMessage.content);
      setIsLoading(false);
      return;
    }

    // Consume 0.5 credit for next segment
    const creditConsumed = await consumeCredits(0.5, `Lesson segment: ${config.topic} - Gap ${nextGapIndex + 1}`);
    if (!creditConsumed) {
      setIsLoading(false);
      return;
    }

    try {
      // Get the next segment prompt
      const nextSegmentPrompt = getAdaptiveTutorNextSegmentPrompt(
        userProfile,
        config.topic,
        gapAnalysis,
        nextGapIndex,
        config.workplace
      );

      // Enhanced system message for next segments
      const systemMessage = `You are BB EdTech's Adaptive Tutor. Create the NEXT PART of a personalized lesson.

IMPORTANT FORMATTING REQUIREMENTS - YOU MUST FOLLOW THESE EXACTLY:

1. **USE TABLES** where appropriate:
   - Quick Fix table with | Mistake | Correct Understanding |
   - Use |---| for column separators

2. **USE MARKDOWN HEADINGS** exactly as shown:
   - ## for main sections (Module X/Y, Key Point, etc.)

3. **USE EMOJIS**: 📍 📚 💼 ✅ ⏭️

4. **KEEP IT CONCISE** - focus on one concept only

Here's the EXACT structure you must follow:

## 📍 Module X/Y: [Topic]

**Question:** [Question text]

**You answered:** "[User's answer]"
**Correct answer:** "[Correct answer]"

---

## 📚 Key Point

[2-3 clear sentences explaining the concept]

**Quick example:** [One simple example]

---

## 💼 Workplace Application

*In [location]:* [1-2 sentences on practical use]

---

## ✅ Quick Fix

| Mistake | Correct Understanding |
|---------|----------------------|
| "[Wrong answer]" | "[Right answer]" |

---

## ⏭️ Next: [Next concept or "Complete!"]

*Module X done. Type "continue" for next (0.5 credits).* OR *All modules complete!*

---

Focus ONLY on the specific gap/misconception from Question ${nextGapIndex + 1}.`;

      const lessonResponse = await callOpenRouterApi(
        nextSegmentPrompt,
        userProfile,
        systemMessage
      );

      const finalLesson = validateLaTeX(lessonResponse);

      const newSegments = [
        ...lessonSegments,
        {
          id: `seg-${Date.now()}-${nextGapIndex}`,
          title: `Understanding ${gapAnalysis[nextGapIndex].topic}`,
          content: finalLesson,
          gapIndex: nextGapIndex,
          requiresCredit: true,
          consumed: true
        }
      ];

      setLessonSegments(newSegments);
      setCurrentGapIndex(nextGapIndex);

      await updateSession(currentSessionId, {
        lesson_segments: newSegments,
        current_gap_index: nextGapIndex
      });

      const lessonMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: finalLesson,
        timestamp: new Date(),
        chat_type: 'adaptive-tutor',
      };

      const newMessages = [...messages, lessonMessage];
      if (isMountedRef.current) {
        setMessages(newMessages);
      }
      await saveChatMessage(currentSessionId, 'assistant', finalLesson);

    } catch (error) {
      console.error('Error generating lesson segment:', error);
      setErrorMessage('Failed to generate next lesson segment.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!input.trim() || isLoading) return;

    if (configStep === 'welcome') {
      // User is entering topic info
      handleStartQuiz();
    } else if (quizState && quizState.isComplete && (input.toLowerCase() === 'continue' || input.toLowerCase() === 'next')) {
      // User wants next lesson segment
      generateNextLessonSegment();
    } else {
      // Regular chat
      handleRegularChat();
    }
  };

  const handleRegularChat = async () => {
    setIsLoading(true);
    setErrorMessage('');

    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
      chat_type: 'adaptive-tutor',
    };

    const newMessages = [...messages, userMessage];
    if (isMountedRef.current) {
      setMessages(newMessages);
    }
    await saveChatMessage(currentSessionId, 'user', input);
    setInput('');

    try {
      const systemMessage = `You are BB EdTech's Adaptive Tutor. You're currently helping a learner with ${config.topic} in ${config.course}${config.workplace ? `, focusing on applications in ${config.workplace}` : ''}. Provide helpful, clear explanations.`;
      
      const response = await callOpenRouterApi(input, userProfile, systemMessage);
      const finalResponse = validateLaTeX(response);

      const assistantMessage: ChatMessageType = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: finalResponse,
        timestamp: new Date(),
        chat_type: 'adaptive-tutor',
      };

      const updatedMessages = [...newMessages, assistantMessage];
      if (isMountedRef.current) {
        setMessages(updatedMessages);
      }
      await saveChatMessage(currentSessionId, 'assistant', finalResponse);

    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Failed to get response. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const switchToSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session) {
      if (isMountedRef.current) {
        setCurrentSessionId(sessionId);
        setMessages(session.messages);
        setConfigStep(session.quiz_completed ? 'quiz' : 'welcome');
        
        if (session.quiz_questions) {
          setQuizState({
            questions: session.quiz_questions,
            currentQuestion: 0,
            answers: session.quiz_answers || [],
            isComplete: true,
            topic: session.topic || '',
            workplace: session.workplace || ''
          });
        }
        
        if (session.lesson_segments) {
          setLessonSegments(session.lesson_segments);
          setCurrentGapIndex(session.current_gap_index || 0);
        }
        
        if (session.topic) {
          setConfig(prev => ({
            ...prev,
            topic: session.topic || '',
            workplace: session.workplace || ''
          }));
        }
      }
    }
  };

  const deleteSession = async (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (session?.isTemporary) {
      if (isMountedRef.current) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    } else {
      await supabase.from('chat_messages').delete().eq('session_id', sessionId);
      await supabase.from('chat_sessions').delete().eq('id', sessionId);
      if (isMountedRef.current) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
      }
    }

    if (currentSessionId === sessionId) {
      const remainingSessions = sessions.filter(s => s.id !== sessionId);
      if (remainingSessions.length > 0) {
        switchToSession(remainingSessions[0].id);
      } else {
        createNewSession();
      }
    }
  };

  const renderCreditDisplay = () => (
    <div className="credit-display">
      <div className="credit-info">
        <div className="credit-icon">💰</div>
        <div className="credit-details">
          <div className="credit-count">{userCredits} credits</div>
          <div className="subscription-tier">{subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} Plan</div>
        </div>
        {userCredits < 3 && (
          <button 
            className="get-more-credits-btn"
            onClick={() => setShowSubscriptionModal(true)}
          >
            Get More
          </button>
        )}
      </div>
      {userCredits < 1 && (
        <div className="credit-warning">
          ⚠️ Low credits! You need at least 1 credit to start a quiz.
        </div>
      )}
    </div>
  );

  const renderConfigForm = () => (
    <div className="config-container">
      <div className="config-header">
        <h2>🎯 Adaptive Learning Configuration</h2>
        <p>Tell me what you want to learn, and I'll create a personalized quiz and lesson plan.</p>
      </div>

      <div className="config-form">
        <div className="form-group">
          <label>Course/Subject *</label>
          <input
            type="text"
            value={config.course}
            onChange={(e) => setConfig(prev => ({...prev, course: e.target.value}))}
            placeholder="e.g., Mathematics, Physics, Business Management"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Topic in Context *</label>
          <input
            type="text"
            value={config.topic}
            onChange={(e) => setConfig(prev => ({...prev, topic: e.target.value}))}
            placeholder="e.g., Differential Calculus, Quantum Mechanics, Marketing Strategy"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Keywords (comma-separated, optional)</label>
          <input
            type="text"
            value={tempKeywords}
            onChange={(e) => setTempKeywords(e.target.value)}
            onBlur={handleKeywordsBlur}
            placeholder="e.g., derivatives, wave functions, consumer behavior"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>Place of Work / Application Field (optional)</label>
          <input
            type="text"
            value={config.workplace}
            onChange={(e) => setConfig(prev => ({...prev, workplace: e.target.value}))}
            placeholder="e.g., Engineering firm, Hospital, Marketing agency"
            className="form-input"
          />
        </div>

        <div className="credit-info-box">
          <div className="credit-cost">
            <span className="cost-badge">💰 1 credit</span>
            <span className="cost-description">for 10-question quiz + first lesson segment</span>
          </div>
          <div className="credit-cost">
            <span className="cost-badge">💰 0.5 credit</span>
            <span className="cost-description">per additional lesson segment</span>
          </div>
        </div>

        <button
          className="start-quiz-btn"
          onClick={handleStartQuiz}
          disabled={isLoading || !config.course.trim() || !config.topic.trim() || userCredits < 1}
        >
          {isLoading ? (
            <>
              <span className="loading-spinner"></span>
              Generating Quiz...
            </>
          ) : userCredits < 1 ? (
            '❌ Insufficient Credits'
          ) : (
            '🚀 Start Assessment Quiz (1 credit)'
          )}
        </button>
      </div>
    </div>
  );

  const renderQuizInterface = () => {
    if (!quizState) return null;

    if (!quizState.isComplete) {
      return (
        <div className="quiz-container">
          <div className="quiz-progress">
            Question {quizState.currentQuestion + 1} of {quizState.questions.length}
          </div>
          <div className="quiz-question">
            {quizState.questions[quizState.currentQuestion].question}
          </div>
          <div className="quiz-options">
            {quizState.questions[quizState.currentQuestion].options.map((option, idx) => (
              <div
                key={idx}
                className="quiz-option"
                onClick={() => handleQuizAnswer(idx)}
              >
                {String.fromCharCode(65 + idx)}. {option}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`adaptive-tutor ${theme}`}>
      <div className="background-container">
        {theme === 'light' ? (
          <div className="morning-sky"></div>
        ) : (
          <div className="night-sky"></div>
        )}
      </div>

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentCredits={userCredits}
      />

      <div className="adaptive-tutor-layout">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-header">
            <div className="logo">🎯</div>
            {sidebarOpen && <span className="app-name">Adaptive Tutor</span>}
          </div>
          
          {sidebarOpen && (
            <>
              <div className="theme-toggle-sidebar">
                <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                  {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                </button>
              </div>
              
              {renderCreditDisplay()}
              
              <button className="new-session-btn" onClick={createNewSession}>
                ➕ New Session
              </button>
              
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Search sessions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="session-history">
                <h4>Learning History</h4>
                {sessions
                  .filter(session => 
                    session.title.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((session) => (
                    <div
                      key={session.id}
                      className={`session-item ${currentSessionId === session.id ? 'active' : ''}`}
                      onClick={() => switchToSession(session.id)}
                    >
                      <div className="session-title">
                        {session.quiz_completed && '✅ '}
                        {session.title}
                        {session.isTemporary && ' (New)'}
                      </div>
                      <div className="session-info">
                        <span className="session-timestamp">
                          {new Date(session.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <button
                        className="delete-session-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSession(session.id);
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        <div className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>

          <div className="chat-header">
            <h1 className="chat-title">🎯 ADAPTIVE TUTOR</h1>
            <p className="chat-tagline">
              <em>Personalized learning that adapts to your knowledge gaps</em>
            </p>
          </div>

          {/* Mobile credit display */}
          <div className="mobile-credit-display">
            {renderCreditDisplay()}
          </div>

          {errorMessage && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{errorMessage}</span>
              {errorMessage.includes('credits') && (
                <button 
                  className="error-upgrade-btn"
                  onClick={() => setShowSubscriptionModal(true)}
                >
                  Get More Credits
                </button>
              )}
            </div>
          )}

          <div className="chat-messages-container" ref={chatContainerRef}>
            {messages.map((message) => (
              <div key={message.id} className="message-wrapper">
                <ChatMessage 
                  message={message}
                  onSpeak={(text) => speak(text, message.id)}
                  isSpeaking={isMessageSpeaking(message.id)}
                  onShare={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/message/${message.id}`);
                  }}
                />
              </div>
            ))}

            {renderQuizInterface()}
            
            {isLoading && (
              <div className="typing-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="typing-text">
                  {isGeneratingQuiz ? 'Generating quiz questions...' : 
                   quizState?.isComplete ? 'Creating personalized lesson...' : 
                   'Processing...'}
                </span>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {configStep === 'welcome' && !quizState ? (
            renderConfigForm()
          ) : (
            quizState && !quizState.isComplete ? null : (
              <InputField
                value={input}
                onChange={setInput}
                onSubmit={handleSubmit}
                loading={isLoading}
                placeholder={
                  quizState?.isComplete && gapAnalysis.some((a, idx) => idx > currentGapIndex && !a.isCorrect)
                    ? 'Type "continue" or "next" for the next lesson segment (0.5 credits)...'
                    : 'Ask a follow-up question or request clarification...'
                }
              />
            )
          )}

          {quizState?.isComplete && currentGapIndex > 0 && (
            <div className="progress-indicator">
              <div className="progress-text">
                Lesson progress: {currentGapIndex + 1} of {gapAnalysis.filter(a => !a.isCorrect).length} segments completed
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${((currentGapIndex + 1) / gapAnalysis.filter(a => !a.isCorrect).length) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdaptiveTutor;